import {useCallback, useEffect} from 'react';
import {useDispatch} from 'react-redux';
import {axiosBffFetch} from '../axios/axiosBffFetch';
import {setIsParentSelfCertificated} from '../user/UserSlice';
import {useLogin} from '../user/useLogin.hook';
import {getIsValidOriginInAuthCompletedEvent} from './getIsValidOrigin';
import {SelfCertCompleteEventData} from './SelfCertCompleteEventData.interface';

/**
 * 인증 완료 페이지에서 발생하는 message 이벤트를 받아서 처리합니다.
 */
export function useSelfCertCompletedEvent() {
  const dispatch = useDispatch();
  const {user} = useLogin();

  const refreshUserAge = useCallback(async (redirectUrl?: string) => {
    // 인증이 성공한 뒤에는 jwt 쿠키를 새로 만들어야 BFF에서 정상적으로 연령 인증을 반영할 수 있으므로 refresh_token을 진행해줍니다.
    await axiosBffFetch('/api/refresh_token', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
      data: {
        needUpdateAge: true,
      },
      withCredentials: true,
      timeout: 5000,
    });

    if (redirectUrl) {
      location.href = redirectUrl;
    } else {
      // 현재는 기존 연령 인증값이 redux에 저장되어 있고, 기존 연령 인증값으로 캐시되어 있는 BFF응답이 있을 수 있으니 refresh_token 완료 후 새로고침합니다.
      // 실패할 경우는 거의 없지만 이 경우는 비정상적인 경우이므로 새로고침을 시킵니다.
      location.reload();
    }
  }, []);
  const handleAuthCompleted = useCallback(
    (event: MessageEvent<SelfCertCompleteEventData>) => {
      /**
       * 일반적인 인증은 자식 window 창에서 인증이 끝나면 postMessage로 인증 정보를 받아와 이 훅에서 처리를 합니다.
       * 하지만 빌링의 경우는 창을 닫지 않고 띄운 자식 window 에서 추가적인 작업을 진행하므로 부모 window 는 인증 정보를 갱신할 수 없습니다.
       * 이런 경우 /auth/id/verify/start 에서 본인인증을 진행하지 않고 이쪽으로 바로 보내줍니다. (인증이 이미 완료 되었으니 Bypass)
       */
      if (event.data.isAlreadyCert) {
        if (event.data.redirectUrl) {
          location.href = event.data.redirectUrl;
        } else {
          location.reload();
        }
        return;
      }

      if (!getIsValidOriginInAuthCompletedEvent(event.origin)) {
        return;
      }
      if (event.data.needUpdateAge) {
        refreshUserAge(event.data.redirectUrl);
        return;
      }
      if (user?.age && event.data.age) {
        const {age} = event.data;
        if (age >= 19) {
          refreshUserAge(event.data.redirectUrl);
          return;
        }
        if (age < 14) {
          dispatch(setIsParentSelfCertificated(true));
          return;
        }
      }
    },
    [dispatch, refreshUserAge, user?.age]
  );
  useEffect(() => {
    window.addEventListener('message', handleAuthCompleted);
    return () => {
      window.removeEventListener('message', handleAuthCompleted);
    };
  }, [handleAuthCompleted]);

  return {handleAuthCompleted};
}
